/* Step 4 — Guest Checkout. Reuses the same Razorpay Checkout.js script load
 * pattern as the Design Library so we don't duplicate state. */
import React, { useState } from "react";
import axios from "axios";
import { ChevronLeft, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

const loadRazorpay = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("razorpay_load_failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("razorpay_load_failed"));
    document.body.appendChild(s);
  });

export default function StepCheckout({ req, preview, onBack, onPaid }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  const phoneDigits = phone.replace(/[^0-9]/g, "").slice(-10);
  const ok = name.trim().length >= 2 && phoneDigits.length === 10;

  const startPayment = async () => {
    setErr("");
    setPaying(true);
    try {
      const { data: order } = await axios.post(`${API}/studio/checkout`, {
        requirements: req,
        customer_name: name.trim(),
        customer_whatsapp: phoneDigits,
        customer_email: email.trim() || undefined,
      });

      // Admin-test bypass path — server returned gateway='bypass_admin_test'
      // meaning the request is ALREADY marked paid via BYPASS_ADMIN_TEST.
      // Skip the Razorpay modal + verify hop entirely.
      if (order.gateway === "bypass_admin_test") {
        onPaid({
          request_id: order.request_id,
          receipt_no: order.receipt_no,
          pdf_url: `/api/studio/requests/${order.request_id}/floor-plan.pdf`,
          png_url: `/api/studio/requests/${order.request_id}/floor-plan.png`,
          svg_url: `/api/studio/requests/${order.request_id}/floor-plan.svg`,
          customer_name: name.trim(),
          customer_whatsapp: phoneDigits,
          amount: order.amount,
          bypass: true,
        });
        return;
      }

      if (order.gateway !== "razorpay" || !order.razorpay_order_id) {
        setErr("Online payment is not configured. Please contact support.");
        setPaying(false);
        return;
      }

      await loadRazorpay();

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Mera Naksha",
        description: `Design Studio · ${req.plot_width}'×${req.plot_depth}' ${req.bhk}BHK`,
        order_id: order.razorpay_order_id,
        prefill: { name: name.trim(), contact: phoneDigits, email: email.trim() || "" },
        notes: { receipt_no: order.receipt_no, request_id: order.request_id, product: "design_studio" },
        theme: { color: "#0B2545" },
        handler: async (response) => {
          try {
            const { data: verified } = await axios.post(`${API}/studio/razorpay-verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            onPaid({
              ...verified,
              customer_name: name.trim(),
              customer_whatsapp: phoneDigits,
              amount: order.amount,
            });
          } catch (e) {
            setErr("Payment captured but verification failed. Contact support with receipt " + order.receipt_no);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setErr("Payment cancelled. You can retry anytime.");
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        setPaying(false);
        const e = resp?.error || {};
        const human = [e.description, e.reason, e.code].filter(Boolean).join(" · ");
        setErr(human || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  };

  return (
    <section data-testid="studio-step-checkout">
      <h2 className="text-2xl sm:text-3xl font-extrabold text-navy">Checkout</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        We'll send the downloads + receipt to your WhatsApp and email.
      </p>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-3xl bg-white border-2 border-border p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
              Your name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Ramesh Kumar"
              data-testid="studio-checkout-name"
              className="mt-1 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-base font-semibold focus:border-navy focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
              WhatsApp number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit mobile"
              maxLength={14}
              data-testid="studio-checkout-phone"
              className="mt-1 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-base font-semibold tabular-nums focus:border-navy focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
              Email (optional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="studio-checkout-email"
              className="mt-1 w-full rounded-xl border-2 border-border bg-white px-4 py-3 text-base font-semibold focus:border-navy focus:outline-none"
            />
          </div>

          {err && (
            <div className="rounded-xl bg-red-50 border-2 border-red-200 px-4 py-3 text-sm font-bold text-red-700" data-testid="studio-checkout-error">
              {err}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="rounded-3xl bg-navy text-white p-5 border-2 border-navy-600 h-fit">
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-bold">Order summary</div>
          <div className="mt-2 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-white/70">Plot</span><span className="font-bold">{req.plot_width}' × {req.plot_depth}'</span></div>
            <div className="flex justify-between"><span className="text-white/70">Configuration</span><span className="font-bold">{req.bhk}BHK · {req.floors}</span></div>
            <div className="flex justify-between"><span className="text-white/70">Base design</span><span className="font-bold text-right">{preview.base_design.name}</span></div>
          </div>
          <hr className="my-4 border-white/15" />
          <div className="flex justify-between items-end">
            <span className="text-[11px] uppercase tracking-wider text-white/60 font-bold">Total</span>
            <span className="text-3xl font-extrabold text-gold tabular-nums">₹{preview.price.toLocaleString("en-IN")}</span>
          </div>
          <div className="mt-3 text-[11px] text-white/60 inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" />
            Secured by Razorpay
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <button
          type="button"
          onClick={onBack}
          disabled={paying}
          data-testid="studio-checkout-back"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border-2 border-border text-navy font-bold hover:border-navy/40 disabled:opacity-50"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </button>
        <button
          type="button"
          onClick={startPayment}
          disabled={!ok || paying}
          data-testid="studio-pay-now"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-navy font-extrabold hover:bg-gold-dark hover:text-white disabled:opacity-50"
        >
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {paying ? "Opening Razorpay…" : `Pay ₹${preview.price.toLocaleString("en-IN")}`}
        </button>
      </div>
    </section>
  );
}
